import {
  convertToInternationalCurrencySystem,
  setNumberFixed,
  magicNumbers,
} from 'utils';
import translation from 'translation';
import { store } from 'store';

const { t } = translation;

export const calculateEngagement = (engagement) => {
  return Number(((engagement ?? 0) * 100).toFixed(2));
};

export const formatUnlessZero = (num) => {
  if (num === null) return null;
  const formatedValue = calculateEngagement(num);
  return String(formatedValue).match(/^(-?0(\.0{1,2})?)$/)
    ? undefined
    : formatedValue;
};

export const calculateWeeks = (params) => {
  const duration = params.time_duration ?? params.to - params.from;
  return duration / magicNumbers.ONE_WEEK_IN_SECONDS;
};

export const calculateWeeklyPublishingRate = (value) => {
  const rounded = parseFloat(Number(value).toFixed(2));
  return rounded;
};

const VOICE_TREND_API_PLATFORM_MAP = {
  instagramComment: 'instagram-comments',
};

export const getVoiceTrendApiPlatform = (platform) => {
  if (!platform || platform === 'all') return null;
  return VOICE_TREND_API_PLATFORM_MAP[platform] ?? platform;
};

export const getVoiceTrendSeriesKey = (platform) => {
  if (!platform || platform === 'all') return 'all_platform';
  return VOICE_TREND_API_PLATFORM_MAP[platform] ?? platform;
};

export const normalizeVoiceTrendChartData = (voiceTrendData, platform) => {
  const seriesKey = getVoiceTrendSeriesKey(platform);
  const emptySeries = [];

  const pickSeries = (seriesGroup) => {
    if (!seriesGroup) return emptySeries;
    if (Array.isArray(seriesGroup)) return seriesGroup;
    return seriesGroup[seriesKey] ?? emptySeries;
  };

  return {
    my_brand_series: {
      current_series: pickSeries(
        voiceTrendData?.my_brand_series?.current_series
      ),
      past_series: pickSeries(voiceTrendData?.my_brand_series?.past_series),
    },
    competitor_series: {
      current_series: pickSeries(
        voiceTrendData?.competitor_series?.current_series
      ),
      past_series: pickSeries(voiceTrendData?.competitor_series?.past_series),
    },
  };
};

export const calculatePercentage = (value, total, roundedNumber) => {
  if (total === 0) {
    return 0;
  }
  return setNumberFixed((value / total) * 100, roundedNumber);
};

export const infoBoxHelper = (
  myBrandCount,
  competitorCount,
  brandName,
  type
) => {
  const total = myBrandCount + competitorCount;
  let icon;
  if (type === 'contents') icon = 'ri-file-list-2-line';
  else if (type === 'comments') icon = 'ri-message-2-line';
  else icon = 'ri-user-3-line';

  return {
    type,
    brandName: decodeURI(brandName),
    myBrandCount,
    competitorCount,
    myBrandPercentage: calculatePercentage(myBrandCount, total, 0),
    competitorPercentage: calculatePercentage(competitorCount, total, 0),
    icon,
  };
};

export const sentimentHelper = (data, competitorName) => {
  const colors = {
    positive: '#36B37E',
    neutral: '#AAAAAA',
    negative: '#FF5630',
  };

  const legends = [
    { key: 'negative', title: t('filter:negitive') },
    { key: 'neutral', title: t('filter:neutral') },
    { key: 'positive', title: t('filter:positive') },
  ];

  const chartData = data.map((item) => ({
    isMyBrand: item.isMyBrand,
    title: item.isMyBrand ? item.title : competitorName,
    query: item.query,
    positive: {
      title: t('filter:positive'),
      percentage: setNumberFixed(item.positive * 100, 2),
    },
    neutral: {
      title: t('filter:neutral'),
      percentage: setNumberFixed(item.neutral * 100, 2),
    },
    negative: {
      title: t('filter:negitive'),
      percentage: setNumberFixed(item.negative * 100, 2),
    },
  }));

  return {
    chartData,
    legends,
    colors,
  };
};

export const distributionChartHelper = (
  myBrandData,
  competitorData,
  competitorName
) => {
  const myBrandSum = myBrandData.reduce((acc, item) => acc + item.value, 0);
  const competitorSum = competitorData.reduce(
    (acc, item) => acc + item.value,
    0
  );

  const chartData = myBrandData
    .map((data) => {
      const competitorItem = competitorData.find(
        (item) => item.platform === data.platform
      );
      return [
        {
          item: data.platform,
          key: 'score1',
          score: calculatePercentage(data.value, myBrandSum, 2),
          color: '#37AAAF',
          count: convertToInternationalCurrencySystem(data.value),
        },
        {
          item: data.platform,
          key: 'score2',
          score: calculatePercentage(competitorItem.value, competitorSum, 2),
          color: '#E05F81',
          count: convertToInternationalCurrencySystem(competitorItem.value),
          competitorName,
        },
      ];
    })
    .flat();

  const legends = [
    { key: 'score1', title: t('brandBaan:contents.radarChart.text1') },
    {
      key: 'score2',
      title: t('brandBaan:contents.radarChart.text2', {
        competitor: competitorName,
      }),
    },
  ];

  return { chartData, legends };
};

/**
 * The line chart always plots a field named `count`, so a series measured in
 * something else (the net sentiment score, for one) names its source field
 * through `valueKey` and is copied into `count` here.
 */
export const trendChartHelper = ({
  chartData,
  chartDataKey,
  translationKey = 'contents.publishingChartLegend',
  translationPerfix,
  competitorName,
  valueKey = 'count',
}) => {
  const effictiveTime = chartData[chartDataKey.myBrand].current_series.map(
    (item) => item.date
  );

  const myBrandPresent = chartData[chartDataKey.myBrand].current_series.map(
    (item, index) => ({
      id: 0,
      count: item[valueKey],
      q: t(`brandBaan:${translationKey}.text1`, {
        perfix: translationPerfix
          ? t(`brandBaan:${translationPerfix}.title`)
          : '',
      }),
      time: effictiveTime[index],
      color: '#37AAAF',
      type: '',
      category: 'myBrand',
    })
  );

  const myBrandPast = chartData[chartDataKey.myBrand].past_series.map(
    (item, index) => ({
      id: 1,
      count: item[valueKey],
      q: t(`brandBaan:${translationKey}.text2`, {
        perfix: translationPerfix
          ? t(`brandBaan:${translationPerfix}.title`)
          : '',
      }),
      time: effictiveTime[index],
      color: '#37AAAF',
      type: 'dashed',
      category: 'myBrand',
      acctualTime: item.date,
    })
  );

  const competitorPresent = chartData[
    chartDataKey.competitor
  ].current_series.map((item, index) => ({
    id: 2,
    count: item[valueKey],
    q: t(`brandBaan:${translationKey}.text3`, {
      perfix: translationPerfix
        ? t(`brandBaan:${translationPerfix}.title`)
        : '',
      competitor: competitorName,
    }),
    time: effictiveTime[index],
    color: '#E05F81',
    type: '',
    category: 'competitor',
  }));

  const competitorPast = chartData[chartDataKey.competitor].past_series.map(
    (item, index) => ({
      id: 3,
      count: item[valueKey],
      q: t(`brandBaan:${translationKey}.text4`, {
        perfix: translationPerfix
          ? t(`brandBaan:${translationPerfix}.title`)
          : '',
        competitor: competitorName,
      }),
      time: effictiveTime[index],
      color: '#E05F81',
      type: 'dashed',
      category: 'competitor',
      acctualTime: item.date,
    })
  );

  const legendPerfix = translationPerfix
    ? t(`brandBaan:${translationPerfix}.title`)
    : '';

  const customLegends = [
    {
      id: 0,
      title: t(`brandBaan:${translationKey}.text1`, {
        perfix: legendPerfix,
      }),
      type: 'solid',
      color: '#37AAAF',
    },
    {
      id: 1,
      title: t(`brandBaan:${translationKey}.text2`, {
        perfix: legendPerfix,
      }),
      type: 'dashed',
      color: '#37AAAF',
    },
    {
      id: 2,
      title: t(`brandBaan:${translationKey}.text3`, {
        perfix: legendPerfix,
        competitor: competitorName,
      }),
      type: 'solid',
      color: '#E05F81',
    },
    {
      id: 3,
      title: t(`brandBaan:${translationKey}.text4`, {
        perfix: legendPerfix,
        competitor: competitorName,
      }),
      type: 'dashed',
      color: '#E05F81',
    },
  ];

  return {
    chartData: [
      ...myBrandPresent,
      ...myBrandPast,
      ...competitorPresent,
      ...competitorPast,
    ],
    legends: customLegends,
  };
};

export const platformsDataHelper = (data) => {
  const mapPlatformToIcon = {
    telegram: 'telegram',
    twitter: 'twitter',
    instagram: 'instagram',
    bale: 'bale-colored',
    eitaa: 'eitaa-colored',
    rubika: 'rubika-colored',
    linkedin: 'linkedin',
    news: 'global-line',
    newspaper: 'Newspaper',
    virasty: 'virasty-line-active',
  };

  return data.map((item) => ({
    ...item,
    value: convertToInternationalCurrencySystem(item.value, 0),
    percentage_change: setNumberFixed(item.percentage_change, 2),
    icon: mapPlatformToIcon[item.platform],
  }));
};

export const adaptTips = (tips) => {
  return tips?.filter((tip) => tip?.value).map((tip) => tip?.value);
};

export const adaptChanges = (change, withSign = true) => {
  const changeSign = withSign && change > 0 ? '+' : '';
  if (change === 0) return '0%';
  if (change > -1 && change < 1) return `${changeSign}${change.toFixed(2)}%`;
  if (change > 1 || change < -1) return `${changeSign}${change.toFixed(1)}%`;
};

export const adaptChangeClass = (change) => {
  if (change === 0) return 'no-change';
  return change > 0 ? 'success' : 'danger';
};

export const pieChartDataHelper = (chartData, brandQuerires) => {
  const myBrandColor = '#199DA3';
  const competitorsColors = [
    '#D22C58',
    '#DC4970',
    '#E05F81',
    '#E8829D',
    '#EF9EB3',
    '#F8CCD8',
    '#FDF0F4',
  ];

  let myBrandIndex;
  const adaptedChartData = chartData
    .sort((a, b) => b.percentage - a.percentage)
    .map((item, index) => {
      if (item.label === 'my-brand') myBrandIndex = index;
      const competitorColorIndex = index > myBrandIndex ? index - 1 : index;

      const brandQuery =
        brandQuerires &&
        brandQuerires.find((query) => query.brand_id === item.brand_id)
          ?.query_id;

      return {
        brandQuery,
        item: item.title,
        count: item.posts_count,
        percent: item.percentage,
        color:
          item.label === 'my-brand'
            ? myBrandColor
            : competitorsColors[competitorColorIndex],
      };
    });

  return adaptedChartData;
};

export const getBrandQuery = (brandId) => {
  const myBrandQuery =
    store.getState().brandBaanReducer.checkStatus.data.queryId;
  const brandQuerires =
    store.getState().brandBaanReducer.checkStatus.data.brandsQuerires;

  const competitorQuery = brandQuerires?.find(
    (b) => b.brand_id === brandId
  )?.query_id;

  return {
    myBrandQuery,
    competitorQuery,
  };
};
